import { PrinterOutlined } from '@ant-design/icons';
import { useIsFetching } from '@tanstack/react-query';
import { Alert, Button, Space, Typography } from 'antd';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { api } from '@/api/client';

/**
 * Больше за один раз не печатаем: каждая форма — отдельный запрос к API,
 * и пачка в несколько сотен упёрлась бы в ограничение частоты запросов.
 */
const MAX_DOCUMENTS = 100;

/**
 * Открывает печатные формы выбранных документов в новой вкладке.
 *
 * Номера идут в адресе, а не в памяти страницы: вкладка открывается отдельно
 * и переживает перезагрузку, а в адресе нет ничего, кроме идентификаторов, —
 * сами документы всё равно отдаёт API с проверкой прав.
 */
export function openBatchPrint(path: string, ids: number[], tableName: string): void {
  window.open(`${path}?ids=${ids.join(',')}`, '_blank', 'noopener');

  // След в журнале действий — как и у выгрузки таблицы (см. StickyTable).
  void api
    .post('/audit/export', { table: tableName, path, format: 'forms', rows: ids.length })
    .catch(() => undefined);
}

/** Идентификаторы документов из адреса: ?ids=12,15,31. */
export function useBatchIds(): string[] {
  const [params] = useSearchParams();
  const raw = params.get('ids') ?? '';

  return useMemo(
    () => [...new Set(raw.split(',').filter((id) => /^\d+$/.test(id)))],
    [raw],
  );
}

interface Props {
  ids: string[];
  /** Печатная форма одного документа — та же, что открывается по одной. */
  render: (id: string) => ReactNode;
}

/**
 * Несколько печатных форм подряд, каждая с новой страницы.
 *
 * Сама форма не дублируется: страница лишь раскладывает готовые документы
 * один под другим и даёт одну общую кнопку печати. Правка макета путевого
 * листа поэтому сразу видна и в одиночной печати, и в пачке.
 */
export function BatchPrintPage({ ids, render }: Props) {
  const { t } = useTranslation();
  // Печать до конца загрузки дала бы пустые страницы на месте недогруженных форм.
  const fetching = useIsFetching();

  const shown = ids.slice(0, MAX_DOCUMENTS);

  return (
    <div className="tms-batch-print">
      <style>{`
        .tms-batch-toolbar {
          position: sticky;
          top: 0;
          z-index: 2;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          max-width: 860px;
          margin: 0 auto;
          padding: 12px 24px;
          border-bottom: 1px solid #eee;
          background: #fff;
        }
        .tms-batch-item + .tms-batch-item {
          border-top: 2px dashed #ccc;
          break-before: page;
        }
        @media print {
          .tms-batch-toolbar, .tms-batch-note { display: none !important; }
          .tms-batch-item + .tms-batch-item { border-top: 0; }
        }
      `}</style>

      <div className="tms-batch-toolbar">
        <Typography.Text type="secondary">
          {t('Документов')}: {shown.length}.{' '}
          {t('Совет: чтобы сохранить в PDF, в диалоге печати выберите «Сохранить как PDF»')}
        </Typography.Text>
        <Space>
          <Button onClick={() => window.close()}>{t('Закрыть')}</Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            loading={fetching > 0}
            disabled={shown.length === 0}
            onClick={() => window.print()}
          >
            {t('Печать')}
          </Button>
        </Space>
      </div>

      {shown.length === 0 && (
        <div className="tms-batch-note" style={{ maxWidth: 860, margin: '0 auto', padding: 24 }}>
          <Alert type="warning" showIcon message={t('Не выбрано ни одного документа')} />
        </div>
      )}

      {ids.length > MAX_DOCUMENTS && (
        <div className="tms-batch-note" style={{ maxWidth: 860, margin: '0 auto', padding: 24 }}>
          <Alert
            type="warning"
            showIcon
            message={`${t('За один раз печатается не больше документов:')} ${MAX_DOCUMENTS}. ${t('Выбрано')}: ${ids.length}`}
          />
        </div>
      )}

      {shown.map((id) => (
        <div key={id} className="tms-batch-item">
          {render(id)}
        </div>
      ))}
    </div>
  );
}
